import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import WORKFLOWS from '@/data/workflows';

const TRIGGER_MS = 450;
const STEP_MS = 420;
const CYCLE_HOLD_MS = 4000;

const STATUS = { queued: 'Queued', running: 'Running', done: 'Done' };

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// One run drives the whole log: every step moves from queued to running
// to done in order, and stays done.
const RunLog = () => {
  const [flowIndex, setFlowIndex] = useState(0);
  const [steps, setSteps] = useState(() => WORKFLOWS[0].tasks.map(() => 'queued'));
  const [triggered, setTriggered] = useState(false);
  const [touched, setTouched] = useState(false);
  const rootRef = useRef(null);
  const tabsRef = useRef(null);
  const [indicator, setIndicator] = useState(null);
  const timers = useRef([]);
  const visible = useRef(false);

  const flow = WORKFLOWS[flowIndex];
  const count = flow.tasks.length;

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const run = useCallback((index) => {
    clearTimers();
    const n = WORKFLOWS[index].tasks.length;
    if (prefersReducedMotion()) {
      setTriggered(true);
      setSteps(Array(n).fill('done'));
      return;
    }
    setTriggered(false);
    setSteps(Array(n).fill('queued'));
    const at = (ms, fn) => timers.current.push(setTimeout(fn, ms));
    at(TRIGGER_MS, () => setTriggered(true));
    for (let i = 0; i < n; i += 1) {
      at(TRIGGER_MS + i * STEP_MS, () =>
        setSteps((prev) => {
          const next = [...prev];
          if (i > 0) next[i - 1] = 'done';
          next[i] = 'running';
          return next;
        })
      );
    }
    at(TRIGGER_MS + n * STEP_MS, () => setSteps(Array(n).fill('done')));
  }, []);

  useEffect(() => {
    if (window.__PRERENDER__) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const was = visible.current;
        visible.current = entry.isIntersecting;
        if (entry.isIntersecting && !was) run(flowIndex);
        if (!entry.isIntersecting) clearTimers();
      },
      { threshold: 0.35 }
    );
    obs.observe(rootRef.current);
    return () => {
      obs.disconnect();
      clearTimers();
    };
    // flowIndex is read once per visibility change on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  // Until the visitor takes over, run each example workflow in turn
  const complete = steps.every((s) => s === 'done');
  useEffect(() => {
    if (touched || !complete || prefersReducedMotion()) return undefined;
    const t = setTimeout(() => {
      if (!visible.current) return;
      const next = (flowIndex + 1) % WORKFLOWS.length;
      setFlowIndex(next);
      run(next);
    }, CYCLE_HOLD_MS);
    return () => clearTimeout(t);
  }, [complete, touched, flowIndex, run]);

  // Slide one highlight between tabs instead of swapping backgrounds
  useLayoutEffect(() => {
    const measure = () => {
      const tab = tabsRef.current?.children[flowIndex + 1];
      if (tab) setIndicator({ x: tab.offsetLeft, w: tab.offsetWidth });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [flowIndex]);

  const choose = (index) => {
    setTouched(true);
    setFlowIndex(index);
    run(index);
  };

  const doneCount = steps.filter((s) => s === 'done').length;
  const last = steps.lastIndexOf('done');
  const elapsed = last >= 0 ? flow.tasks[last].time : '0.0s';

  return (
    <div className="runlog" ref={rootRef} style={{ '--step-ms': `${STEP_MS}ms` }} data-ui data-testid="run-log">
      <div className="runlog-controls">
        <div className="runlog-tabs" role="tablist" aria-label="Example workflows" ref={tabsRef}>
          <span
            className="runlog-tab-indicator"
            aria-hidden="true"
            style={indicator ? { width: `${indicator.w}px`, transform: `translateX(${indicator.x}px)`, opacity: 1 } : undefined}
          />
          {WORKFLOWS.map((w, i) => (
            <button
              key={w.id}
              type="button"
              role="tab"
              aria-selected={i === flowIndex}
              className="runlog-tab"
              onClick={() => choose(i)}
            >
              {w.tab}
            </button>
          ))}
        </div>
        <button type="button" className="runlog-run" onClick={() => choose(flowIndex)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4.5v15l12-7.5z" fill="currentColor" /></svg>
          Run workflow
        </button>
      </div>

      <div className="runlog-sheet">
        <div className="runlog-top">
          <div>
            <span className="runlog-key">Workflow run</span>
            <span className="runlog-id">{flow.runId}</span>
          </div>
          <span className={`runlog-status${triggered && complete ? ' is-done' : triggered ? ' is-running' : ''}`}>
            {triggered && complete ? `Completed in ${elapsed}` : triggered ? 'Running' : 'Waiting for trigger'}
          </span>
        </div>

        <dl className="runlog-meta">
          <div>
            <dt>Trigger</dt>
            <dd>{flow.trigger}</dd>
          </div>
          <div>
            <dt>Outcome</dt>
            <dd>{flow.outcome}</dd>
          </div>
        </dl>

        <ol className="runlog-steps" key={flow.id} aria-live="polite">
          {flow.tasks.map((task, i) => (
            <li key={`${flow.id}-${i}`} className={`step is-${steps[i]}`}>
              <span className="step-sys">{task.sys}</span>
              <span className="step-label">{task.label}</span>
              <span className="step-status">{STATUS[steps[i]]}</span>
            </li>
          ))}
        </ol>

        <div className="runlog-foot">
          <span>{`${doneCount} of ${count} steps done`}</span>
          <span>Example workflow</span>
        </div>

      </div>
    </div>
  );
};

export default RunLog;
